import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "@layouts/index";
import {
  getTransactionByReference,
  downloadReceiptPDF,
  respondToHostRequest,
} from "@services/krent.service";
import { toast } from "react-toastify";
import DamageReportModal from "../../components/DamageReportModal";

const RequestBookingDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const [responding, setResponding] = useState<"accept" | "decline" | null>(
    null,
  );

  const [showDamageReport, setShowDamageReport] = useState(false);
  const [damageReported, setDamageReported] = useState(false);

  const canRespond =
    transaction?.status === "pending_host_confirmation" ||
    transaction?.status === "paid_pending_confirmation";

  const cautionFee = Number(transaction?.bill?.[0]?.cautionFee) || 0;
  const canReportDamage =
    ["confirmed", "completed"].includes(transaction?.status) &&
    (!transaction?.type || transaction.type === "initial") &&
    cautionFee > 0 &&
    !["paid to renter", "disputed"].includes(transaction?.cautionFeeStatus);

  // Calculate nights from arrival and departure dates
  const calculateNights = () => {
    if (!transaction?.arrival_date || !transaction?.departure_date) return 0;
    const arrival = new Date(transaction.arrival_date);
    const departure = new Date(transaction.departure_date);
    const diffTime = Math.abs(departure.getTime() - arrival.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const nights = calculateNights();

  const paymentReference = transaction?.payment?.[0]?.reference;

  const formatStatus = (status: string) => {
    if (status === "timeout_unconfirmed") return "timeout";
    if (status === "pending_payment") return "pending payment";
    return status;
  };

  const getStatusColor = (status: string) => {
    if (status === "confirmed" || status === "Accepted") return "#1B9E78";
    if (status === "failed" || status === "cancelled" || status === "Rejected")
      return "#DC2626";
    return "#92400e";
  };

  const getStatusBgColor = (status: string) => {
    if (status === "confirmed" || status === "Accepted") return "#e6f7f1";
    if (status === "failed" || status === "cancelled" || status === "Rejected")
      return "#FEE2E2";
    return "#fef3c7";
  };

  const handleDownloadReceipt = async () => {
    if (!paymentReference) return;

    setDownloading(true);
    try {
      const blob = await downloadReceiptPDF(paymentReference);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "krent-receipt.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download receipt. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleHostResponse = async (action: "accept" | "decline") => {
    if (!transaction?.id) return;
    if (
      action === "decline" &&
      !window.confirm(
        "Decline this booking? The guest will be told these dates are not available.",
      )
    ) {
      return;
    }

    setResponding(action);
    try {
      const response: any = await respondToHostRequest(transaction.id, action);
      if (response?.errors?.length) {
        throw new Error(response.errors[0]?.message || "Could not update this booking");
      }
      const nextStatus = response?.data?.status;
      if (nextStatus) {
        setTransaction((prev: any) =>
          prev ? { ...prev, status: nextStatus } : prev,
        );
      }
      toast.success(
        action === "accept"
          ? "Booking accepted. The guest has been notified."
          : "Booking declined. The guest has been notified.",
      );
    } catch (error: any) {
      toast.error(
        error?.errors?.[0]?.message ||
          error?.message ||
          "Could not update this booking. Please try again.",
      );
    } finally {
      setResponding(null);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const fetchTransaction = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response: any = await getTransactionByReference(id);
        setTransaction(response.data);
      } catch (error) {
        console.error("Failed to fetch transaction:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();

    return () => {
      controller.abort();
    };
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout
        title="Booking Details"
        subtitle="View booking information"
      >
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
          Loading…
        </div>
      </DashboardLayout>
    );
  }

  if (!transaction) {
    return (
      <DashboardLayout
        title="Booking Details"
        subtitle="View booking information"
      >
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
          Transaction not found
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Request Details" subtitle="View request details">
      <div className=" min-vh-100 pb-4 px-2">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-start gap-3 mb-4">
          <button
            className="btn btn-link text-dark text-decoration-none d-flex align-items-center gap-2 p-0"
            style={{ fontSize: "14px" }}
            onClick={() => navigate("/requests")}
          >
            <span>&#8592;</span> Back to my bookings
          </button>
          <button
            className="btn d-flex align-items-center gap-2 w-max w-md-auto"
            style={{
              backgroundColor: "#47BBA9",
              color: "#fff",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 500,
              padding: "10px 18px",
            }}
            onClick={handleDownloadReceipt}
            disabled={downloading || !paymentReference}
          >
            <i className="fa-regular fa-download text-white"></i>
            {downloading ? " Downloading..." : " Download receipt"}
          </button>
        </div>

        {canRespond && (
          <div
            className="bg-white rounded-4 p-4 mb-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3"
            style={{ border: "0.5px solid #E5E7EB" }}
          >
            <div>
              <p className="fw-semibold mb-1" style={{ fontSize: 15 }}>
                {transaction?.status === "paid_pending_confirmation"
                  ? "This guest has paid. Confirm if the property is available."
                  : "A guest is waiting for you to confirm this booking."}
              </p>
              <p className="mb-0" style={{ fontSize: 13, color: "#6B7280" }}>
                Please accept or decline within the response window.
              </p>
            </div>
            <div className="d-flex gap-2 w-100 w-md-auto">
              <button
                type="button"
                className="btn flex-fill"
                style={{
                  backgroundColor: "#47BBA9",
                  color: "#fff",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "10px 18px",
                }}
                disabled={!!responding}
                onClick={() => handleHostResponse("accept")}
              >
                {responding === "accept" ? "Accepting..." : "Accept"}
              </button>
              <button
                type="button"
                className="btn flex-fill"
                style={{
                  backgroundColor: "#fff",
                  color: "#DC2626",
                  border: "1px solid #FECACA",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "10px 18px",
                }}
                disabled={!!responding}
                onClick={() => handleHostResponse("decline")}
              >
                {responding === "decline" ? "Declining..." : "Decline"}
              </button>
            </div>
          </div>
        )}

        <div
          className="bg-white rounded-4 p-4 mb-3"
          style={{ border: "0.5px solid #E5E7EB" }}
        >
          <div className="d-flex flex-column flex-md-row align-items-start gap-3">
            <div
              style={{
                width: 80,
                height: 70,
                borderRadius: 10,
                overflow: "hidden",
                flexShrink: 0,
                background: "linear-gradient(135deg, #a8d8ea, #c8e6c9)",
              }}
            >
              {transaction?.listing?.coverImage ||
              transaction?.listing?.otherImages?.[0] ? (
                <img
                  src={
                    transaction.listing.coverImage ||
                    transaction.listing.otherImages[0]
                  }
                  alt="Property"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #a8d8ea, #c8e6c9)",
                  }}
                >
                  <i
                    className="fa-solid fa-house text-white"
                    style={{ fontSize: "32px" }}
                  ></i>
                </div>
              )}
            </div>

            {/* Property Info */}
            <div className="flex-grow-1">
              <p className="fw-semibold mb-1" style={{ fontSize: 16 }}>
                {transaction?.listing?.title || "Property"}
              </p>
              <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
                <span
                  className="d-flex align-items-center gap-1"
                  style={{ fontSize: 13 }}
                >
                  <span style={{ color: "#f5a623" }}>★</span>
                  <strong>{transaction?.listing?.rating || "0.0"}</strong>
                  <span className="text-black">
                    ({transaction?.listing?.reviews || "0"} reviews)
                  </span>
                </span>
                <span className="text-black" style={{ fontSize: 13 }}>
                  📍{" "}
                  <a href="#" className="text-black">
                    {transaction?.listing?.location?.formattedAddress ||
                      "Location"}
                  </a>
                </span>
                <span className="text-black" style={{ fontSize: 13 }}>
                  🗂{" "}
                  <a href="#" className="text-black">
                    Ref: {transaction?.referenceId || "N/A"}
                  </a>
                </span>
              </div>
              <span
                className="badge"
                style={{
                  backgroundColor: getStatusBgColor(transaction?.status),
                  color: getStatusColor(transaction?.status),
                  fontWeight: 500,
                  fontSize: 12,
                  borderRadius: 20,
                  padding: "4px 12px",
                }}
              >
                {formatStatus(transaction?.status) || "Pending"}
              </span>
            </div>
            <div
              className={` rounded-4`}
              style={{ backgroundColor: "#47BBA90D" }}
            >
              <span className="badge-non-instant font-onest d-flex gap-2 py-2 px-3">
                <div
                  className="bg-primary p-1 rounded-full d-flex align-items-center justify-content-center"
                  style={{ width: 20, height: 20 }}
                >
                  <i
                    className="fa-house fa-regular text-white"
                    style={{ fontSize: 10 }}
                  />
                </div>
                {transaction.listing?.isInstant ? "Instant" : "Non Instant"}{" "}
                Property
              </span>
            </div>
          </div>

          <hr style={{ borderColor: "#E5E7EB", margin: "16px 0" }} />

          <p className="fw-semibold mb-3" style={{ fontSize: 15 }}>
            Property details
          </p>
          <div className="d-flex flex-wrap gap-3">
            {[
              {
                icon: "fa-solid fa-users",
                label: `${transaction?.guest?.adult || transaction?.listing?.guests || 0} Guests`,
              },
              {
                icon: "fa-solid fa-bed",
                label: `${transaction?.listing?.bedrooms || 0} Bedrooms`,
              },
              {
                icon: "fa-solid fa-bath",
                label: `${transaction?.listing?.bathrooms || 0} Bathrooms`,
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="d-flex align-items-center gap-2"
                style={{
                  fontSize: 13,
                }}
              >
                <i
                  className={item.icon}
                  style={{ fontSize: "14px", color: "#47BBA9" }}
                ></i>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div
          className="bg-white rounded-4 p-4 mb-3"
          style={{ border: "0.5px solid #E5E7EB" }}
        >
          <p className="fw-semibold mb-3" style={{ fontSize: 15 }}>
            Stay details
          </p>

          <div className="row g-3 mb-4">
            {[
              {
                icon: "fa-solid fa-calendar-days",
                label: "CHECK-IN",
                value: transaction?.arrival_date
                  ? new Date(transaction.arrival_date).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric", year: "numeric" },
                    )
                  : "-",
              },
              {
                icon: "fa-solid fa-calendar-days",
                label: "CHECK-OUT",
                value: transaction?.departure_date
                  ? new Date(transaction.departure_date).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric", year: "numeric" },
                    )
                  : "-",
              },
              {
                icon: "fa-solid fa-moon",
                label: "NIGHTS",
                value: `${nights} nights`,
              },
              {
                icon: "fa-solid fa-user",
                label: "GUESTS",
                value: `${transaction?.guest?.adult || transaction?.listing?.guests || 0} guests`,
              },
            ].map((item, idx) => (
              <div className="col-6 col-md-3" key={idx}>
                <div
                  className="rounded-3 p-3"
                  style={{
                    border: "0.5px solid #E5E7EB",
                    backgroundColor: "#F6FBFA",
                  }}
                >
                  <p
                    className=" mb-2 d-flex align-items-center gap-1"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    <i
                      className={item.icon}
                      style={{ fontSize: "12px", color: "#47BBA9" }}
                    ></i>{" "}
                    {item.label}
                  </p>
                  <p className="mb-0 fw-medium" style={{ fontSize: 15 }}>
                    {item.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {[
            {
              label: `₦${nights > 0 ? Math.round((transaction?.bill?.[0]?.propertyPrice || transaction?.listing?.price || 0) / nights).toLocaleString() : transaction?.listing?.price?.toLocaleString() || "0"} × ${nights} nights`,
              amount: `₦${transaction?.bill?.[0]?.propertyPrice?.toLocaleString() || transaction?.listing?.price?.toLocaleString() || "0"}`,
            },
            {
              label: "Service fee",
              amount: `₦${transaction?.bill?.[0]?.serviceCharge?.toLocaleString() || "0"}`,
            },
            {
              label: "Caution fee (refundable)",
              amount: `₦${transaction?.bill?.[0]?.cautionFee?.toLocaleString() || transaction?.listing?.cautionFee?.toLocaleString() || "0"}`,
            },
          ].map((row, idx) => (
            <div
              key={idx}
              className="d-flex justify-content-between align-items-center py-2"
              style={{ fontSize: 14 }}
            >
              <span>{row.label}</span>
              <span>{row.amount}</span>
            </div>
          ))}

          <div
            className="d-flex justify-content-between align-items-center pt-3 mt-1"
            style={{
              borderTop: "0.5px solid #E5E7EB",
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            <span>Total paid</span>
            <span>
              ₦{transaction?.bill?.[0]?.total?.toLocaleString() || "0"}
            </span>
          </div>
        </div>

        {canReportDamage && (
          <div
            className="bg-white rounded-4 p-4 mb-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3"
            style={{ border: "0.5px solid #E5E7EB" }}
          >
            <div>
              <p className="fw-semibold mb-1" style={{ fontSize: 15 }}>
                {damageReported
                  ? "Damage reported — Krent is reviewing"
                  : `Caution fee ₦${cautionFee.toLocaleString("en-NG")} held by Krent`}
              </p>
              <p className="mb-0" style={{ fontSize: 13, color: "#6B7280" }}>
                {damageReported
                  ? "We'll inspect your report before refunding the guest and let you know the outcome."
                  : "If the guest damaged anything, report it before the caution fee is refunded. Once the guest asks for their refund, you have 48 hours."}
              </p>
            </div>
            {!damageReported && (
              <button
                type="button"
                className="btn flex-shrink-0"
                style={{
                  backgroundColor: "#fff",
                  color: "#B45309",
                  border: "1px solid #FCD34D",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "10px 18px",
                }}
                onClick={() => setShowDamageReport(true)}
              >
                Report damage
              </button>
            )}
          </div>
        )}

        {showDamageReport && transaction?.id && (
          <DamageReportModal
            transactionId={transaction.id}
            cautionFee={cautionFee}
            onClose={() => setShowDamageReport(false)}
            onReported={() => setDamageReported(true)}
          />
        )}

        {/* Guest Details Card */}
        <div
          className="bg-white rounded-3 p-4"
          style={{ border: "0.5px solid #E5E7EB" }}
        >
          <p className="fw-semibold mb-3" style={{ fontSize: 15 }}>
            Guest details
          </p>
          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 40,
                height: 40,
                backgroundColor: "#d0ede5",
                color: "#1B9E78",
                fontWeight: 600,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {transaction?.renter?.firstname?.[0]}
              {transaction?.renter?.lastname?.[0]}
            </div>
            <span style={{ fontSize: 15, fontWeight: 500 }}>
              {transaction?.renter?.firstname} {transaction?.renter?.lastname}
            </span>
          </div>
          <div
            className="rounded-3 p-3"
            style={{
              border: "0.5px solid #E5E7EB",
              minHeight: 80,
              fontSize: 13,
              color: "#aaa",
            }}
          >
            {transaction?.specialRequests || "No special requests"}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RequestBookingDetail;
